import { Dispatch, ReactElement, SetStateAction, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { Button } from "@/components/_atoms/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/_atoms/dialog";
import { Separator } from "@/components/_atoms/separator";
import { IconCheckmark } from "@/components/_icons/IconCheckmark";
import { IconChevronDown } from "@/components/_icons/IconChevronDown";
import { IconPlus } from "@/components/_icons/IconPlus";
import { Combobox, ComboboxOption } from "@/components/_molecules/combobox/combobox";
import { PaginationControls } from "@/components/_molecules/pagination-controls";
import { useDomainTranslation } from "@/domain/_shared/i18n/use-domain-translation";
import { useAddAnnualMunicipalPropertyConsumption } from "@/domain/municipal-property/hooks/api/use-add-annual-municipal-property-consumption";
import { useAddMunicipalPropertyInventorySelection } from "@/domain/municipal-property/hooks/api/use-add-municipal-property-inventory-selection";
import { useMunicipalPropertyList } from "@/domain/municipal-property/hooks/api/use-municipal-property-list";
import { useMunicipalPropertyYearMutations } from "@/domain/municipal-property/hooks/api/use-municipal-property-year-mutations";
import { useRecoverMunicipalPropertyYear } from "@/domain/municipal-property/hooks/api/use-recover-municipal-property-year";
import { useCreateAndAddMunicipalProperty } from "@/domain/municipal-property/hooks/use-create-and-add-municipal-property";
import { buildEmptyMunicipalPropertyCreateForm } from "@/domain/municipal-property/models/empty-municipal-property-create-form";
import { MunicipalPropertyCatalogRow } from "@/domain/municipal-property/models/municipal-property-catalog-row.model";
import { MunicipalPropertyCreateFormValues } from "@/domain/municipal-property/models/municipal-property-create-form-values";
import { MUNICIPAL_PROPERTY_SEARCH_DEBOUNCE_MS } from "@/domain/municipal-property/models/municipal-property-search-debounce-ms";
import { toCreateMunicipalPropertyInput } from "@/domain/municipal-property/models/to-create-municipal-property-input";
import { WorkspaceMunicipalityOption } from "@/domain/municipal-property/models/workspace-municipality-option.model";
import { MunicipalPropertyLifecycleFilter } from "@/graphql/generated/graphql";
import { useDebounce } from "@/hooks/use-debounce";
import { extractGraphQLErrorCode } from "@/lib/extract-graphql-error-code";
import { extractGraphQLErrorReasonList } from "@/lib/extract-graphql-error-reason-list";
import { useMutation } from "@tanstack/react-query";

import { MunicipalPropertyCreateForm } from "./municipal-property-create-form";

const MAX_VISIBLE_OPTION_COUNT: number = 50;

type MunicipalPropertyYearAddDialogProps = {
  workspaceId: string;
  onAdded?: (propertyYearId: string) => void;
  onSelectionAdded?: () => void;
  isAvailable?: boolean;
  creation?: {
    year: number;
    municipalityList: readonly WorkspaceMunicipalityOption[];
  };
} & (
  | { registerId: string; year?: never; inventoryId?: never }
  | { registerId?: never; year: number; inventoryId?: string }
);

export function MunicipalPropertyYearAddDialog({
  registerId,
  inventoryId,
  workspaceId,
  year,
  onAdded,
  onSelectionAdded,
  creation,
  isAvailable = true,
}: MunicipalPropertyYearAddDialogProps): ReactElement {
  const { t }: ReturnType<typeof useDomainTranslation> = useDomainTranslation("municipal-property");
  const { t: tCommon }: ReturnType<typeof useTranslation<"common">> = useTranslation("common");
  const [isCreating, setIsCreating] = useState(false);
  const [isOpen, setIsOpen]: [boolean, Dispatch<SetStateAction<boolean>>] = useState(false);
  const isMultiSelect = inventoryId !== undefined;
  const [selectedPropertyList, setSelectedPropertyList] = useState<ComboboxOption<MunicipalPropertyCatalogRow>[]>([]);
  const bulkAddition = useAddMunicipalPropertyInventorySelection();
  const [searchQuery, setSearchQuery]: [string, Dispatch<SetStateAction<string>>] = useState("");
  const [search, setSearch]: [string, Dispatch<SetStateAction<string>>] = useState("");
  const [optionPage, setOptionPage]: [number, Dispatch<SetStateAction<number>>] = useState<number>(0);
  const {
    propertyList,
    isLoading,
    error,
    refetch,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
  }: ReturnType<typeof useMunicipalPropertyList> = useMunicipalPropertyList({
    workspaceId,
    isEnabled: isOpen && !(isCreating && creation),
    filter: {
      inventoryId,
      municipalityId: null,
      search,
      excludeRegisterId: registerId,
      usage: "ALL",
      lifecycle: MunicipalPropertyLifecycleFilter.Active,
    },
  });
  const recoverMembership = useRecoverMunicipalPropertyYear();
  const addAnnualConsumption = useAddAnnualMunicipalPropertyConsumption();
  const { add }: ReturnType<typeof useMunicipalPropertyYearMutations> = useMunicipalPropertyYearMutations();
  const createAndAdd = useCreateAndAddMunicipalProperty({
    addProperty: addCreatedProperty,
    scopeKey: JSON.stringify([workspaceId, creation?.year ?? registerId ?? year, isOpen, isCreating, !!creation]),
  });
  const addition = useMutation({ mutationFn: addProperty, retry: false });
  const isSaving = createAndAdd.mutation.isPending || addition.isPending || bulkAddition.isPending;
  const hasInitialError: boolean = !!error && !isFetchNextPageError;
  const handleDebouncedSearchChange: (query: string) => void = useDebounce(
    (query: string | undefined): void => setSearch(query ?? ""),
    MUNICIPAL_PROPERTY_SEARCH_DEBOUNCE_MS,
  );
  const pageCount: number = Math.ceil(propertyList.length / MAX_VISIBLE_OPTION_COUNT);
  const currentPage: number = Math.min(optionPage, Math.max(0, pageCount - 1));
  const optionList: ComboboxOption<MunicipalPropertyCatalogRow>[] = propertyList
    .slice(currentPage * MAX_VISIBLE_OPTION_COUNT, (currentPage + 1) * MAX_VISIBLE_OPTION_COUNT)
    .map(
      (property: MunicipalPropertyCatalogRow): ComboboxOption<MunicipalPropertyCatalogRow> => ({
        id: property.id,
        value: property.name,
        original: property,
      }),
    );

  function handleSearchChange(query: string): void {
    setSearchQuery(query);
    setOptionPage(0);
    handleDebouncedSearchChange(query);
  }

  function handleOpenChange(open: boolean): void {
    if (isSaving) return;
    if (open) setIsOpen(true);
    else closeDialog();
  }

  function closeDialog(): void {
    setIsOpen(false);
    setIsCreating(false);
    setSelectedPropertyList([]);
    setSearchQuery("");
    setSearch("");
    setOptionPage(0);
    handleDebouncedSearchChange("");
  }

  async function addProperty(propertyId: string): Promise<string> {
    return registerId !== undefined
      ? (await add.mutateAsync({ registerId, propertyId })).addMunicipalPropertyYear.id
      : (
          await addAnnualConsumption.mutateAsync({
            workspaceId,
            year,
            propertyId,
            ...(inventoryId ? { inventoryId } : {}),
          })
        ).addAnnualMunicipalPropertyConsumption.id;
  }

  async function addCreatedProperty(propertyId: string): Promise<string> {
    try {
      return await addProperty(propertyId);
    } catch (error) {
      const recoveredId = await recoverMembership({
        workspaceId,
        propertyId,
        ...(registerId !== undefined ? { registerId } : { year, ...(inventoryId ? { inventoryId } : {}) }),
      }).catch(() => null);
      if (!recoveredId) throw error;
      return recoveredId;
    }
  }

  async function handleCreate(values: MunicipalPropertyCreateFormValues): Promise<void> {
    await createAndAdd.mutation
      .mutateAsync(toCreateMunicipalPropertyInput({ workspaceId, values }), {
        onSuccess: handleAdded,
      })
      .catch(() => undefined);
  }

  function handleAdded(propertyYearId: string): void {
    toast.success(t("register.property.addSuccess"));
    closeDialog();
    onAdded?.(propertyYearId);
  }

  async function handleAdd(): Promise<void> {
    if (isLoading || hasInitialError || isSaving) return;
    if (!selectedPropertyList.length) return;
    try {
      if (inventoryId) {
        await bulkAddition.mutateAsync(
          { workspaceId, inventoryId, propertyIdList: selectedPropertyList.map((property) => property.id) },
          {
            onSuccess: () => {
              toast.success(t("register.property.addSelectionSuccess", { count: selectedPropertyList.length }));
              closeDialog();
              onSelectionAdded?.();
            },
          },
        );
      } else {
        await addition.mutateAsync(selectedPropertyList[0].id, { onSuccess: handleAdded });
      }
    } catch (error) {
      if (extractGraphQLErrorReasonList(error).includes("MISSING_MUNICIPAL_PROPERTY_HISTORY")) {
        toast.error(t("register.property.missingStand"), {
          description: t("register.property.addMissingStand"),
        });
        return;
      }
      toast.error(
        t(
          extractGraphQLErrorCode(error) === "CONFLICT"
            ? "register.property.addConflict"
            : "register.property.addError",
        ),
      );
    }
  }

  const trigger = isAvailable ? (
    <Button size="xs" className="gap-2 [&_svg]:size-3.5" onClick={(): void => setIsOpen(true)}>
      <IconPlus data-icon="inline-start" className="shrink-0" />
      {t("register.property.add")}
    </Button>
  ) : null;

  if (isCreating && creation) {
    return (
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        {trigger}
        <DialogContent className="max-h-[90dvh] max-w-2xl overflow-y-auto" isCloseDisabled={isSaving}>
          <DialogHeader>
            <DialogTitle>{t("create.dialogTitle")}</DialogTitle>
            <DialogDescription>{t("register.property.createDescription")}</DialogDescription>
          </DialogHeader>
          <MunicipalPropertyCreateForm
            municipalityList={creation.municipalityList}
            defaultValues={{
              ...buildEmptyMunicipalPropertyCreateForm(),
              effectiveYear: creation.year,
            }}
            maxEffectiveYear={creation.year}
            submitLabel={t(
              createAndAdd.hasCreatedProperty ? "register.property.retryAdd" : "register.property.createAndAdd",
            )}
            cancelLabel={t("register.property.backToSelection")}
            isFieldsDisabled={createAndAdd.hasCreatedProperty || createAndAdd.hasUncertainCreate}
            isSubmitDisabled={createAndAdd.hasUncertainCreate}
            alert={
              createAndAdd.mutation.isError && (
                <p role="alert" className="text-sm text-destructive">
                  {t(
                    createAndAdd.hasUncertainCreate
                      ? "register.property.checkCatalogAfterError"
                      : createAndAdd.hasAddError
                        ? "register.property.createdButNotAdded"
                        : extractGraphQLErrorCode(createAndAdd.mutation.error) === "CONFLICT"
                          ? "history.conflict"
                          : "create.error",
                  )}
                </p>
              )
            }
            onSubmit={handleCreate}
            onCancel={() => {
              if (createAndAdd.hasUncertainCreate) void refetch();
              setIsCreating(false);
            }}
          />
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {trigger}
      <DialogContent isCloseDisabled={isSaving}>
        <DialogHeader>
          <DialogTitle>{t("register.property.addTitle")}</DialogTitle>
          <DialogDescription>{t("register.property.addDescription")}</DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <p role="status" className="text-sm text-muted-foreground">
            {t("catalog.loading")}
          </p>
        ) : null}
        {hasInitialError ? (
          <div className="flex flex-col gap-2">
            <p role="alert" className="text-sm text-destructive">
              {tCommon("errors.pageError")}
            </p>
            <Button variant="outline" size="xs" onClick={(): void => void refetch()}>
              {t("register.property.retry")}
            </Button>
          </div>
        ) : null}
        <Combobox
          asChild
          modal
          options={optionList}
          checkedIdList={isMultiSelect ? selectedPropertyList.map((property) => property.id) : undefined}
          closeOnSelect={!isMultiSelect}
          allowDeselect={isMultiSelect}
          value={isMultiSelect ? null : (selectedPropertyList[0] ?? null)}
          onSelect={(option) => {
            if (isSaving) return;
            setSelectedPropertyList((current) => {
              if (!option) return [];
              if (!isMultiSelect) return [option];
              return current.some((property) => property.id === option.id)
                ? current.filter((property) => property.id !== option.id)
                : [...current, option];
            });
          }}
          searchQuery={searchQuery}
          onSearchChange={handleSearchChange}
          placeholder={t("register.property.searchPlaceholder")}
          renderItem={function renderCatalogOption(option: ComboboxOption<MunicipalPropertyCatalogRow>): ReactElement {
            return (
              <div className="flex min-h-[36px] w-full items-center justify-between gap-2 px-2 py-0.5 [&_svg]:size-4">
                <span className="truncate text-sm">{option.value}</span>
                {selectedPropertyList.some((property) => property.id === option.id) && (
                  <IconCheckmark className="ml-auto shrink-0" />
                )}
              </div>
            );
          }}
          renderHeader={
            hasNextPage || isFetchNextPageError || pageCount > 1
              ? function renderOptionPagination(): ReactElement {
                  return (
                    <>
                      <div className="p-2">
                        <PaginationControls
                          page={currentPage}
                          pageCount={pageCount}
                          onPrevious={(): void => setOptionPage(currentPage - 1)}
                          onNext={(): void => setOptionPage(currentPage + 1)}
                        />
                        {isFetchNextPageError ? (
                          <p role="alert" className="text-sm text-destructive">
                            {t("register.property.loadMoreError")}
                          </p>
                        ) : null}
                        {hasNextPage || isFetchNextPageError ? (
                          <Button
                            variant="outline"
                            size="xs"
                            disabled={isFetchingNextPage}
                            onClick={(): void => void fetchNextPage()}
                          >
                            {t(isFetchNextPageError ? "register.property.retry" : "catalog.loadMore")}
                          </Button>
                        ) : null}
                      </div>
                      <Separator />
                    </>
                  );
                }
              : undefined
          }
        >
          <Button
            variant="outline"
            disabled={isSaving || isLoading || hasInitialError}
            role="combobox"
            aria-label={t("register.property.selectLabel")}
            className="w-full justify-between [&_svg]:size-4"
          >
            <span className="truncate">
              {isMultiSelect && selectedPropertyList.length
                ? t("register.property.selectedCount", { count: selectedPropertyList.length })
                : (selectedPropertyList[0]?.value ?? t("register.property.selectPlaceholder"))}
            </span>
            <IconChevronDown data-icon="inline-end" className="ml-2 shrink-0 opacity-50" />
          </Button>
        </Combobox>
        {creation && (
          <Button
            variant="ghost"
            size="xs"
            className="justify-self-start gap-2"
            disabled={isSaving}
            onClick={() => {
              createAndAdd.reset();
              setIsCreating(true);
            }}
          >
            <IconPlus data-icon="inline-start" />
            {t("register.property.createNew")}
          </Button>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="xs" disabled={isSaving} onClick={(): void => handleOpenChange(false)}>
            {t("create.cancel")}
          </Button>
          <Button
            size="xs"
            disabled={
              isSaving ||
              !selectedPropertyList.length ||
              add.isPending ||
              addAnnualConsumption.isPending ||
              isLoading ||
              hasInitialError
            }
            onClick={handleAdd}
          >
            {t("register.property.add")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
